import { FieldProps } from './types'

import React, { ReactNode } from 'react'

import { RegisterOptions } from 'react-hook-form/dist/types/validator'

import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import Typography from '@mui/material/Typography'

import { Input } from 'components/UI/Input'
import { FieldLabel } from 'components/UI/FieldLabel'

interface FieldTextProps extends Omit<FieldProps, 'startAdornment'> {
  name: string
  label?: string
  /**
   * Renders the label through the shared `FieldLabel` (the standard gap +
   * "(optional)" suffix) instead of the legacy bare inline label. Opt-in so
   * existing callers that rely on the old label sitting INLINE beside the
   * field (`TransferOwnership.tsx`'s "Sale price" row forces
   * `.MuiFormControl-root` into a row layout) are byte-for-byte unaffected —
   * only VERA-291's Profile call sites pass this.
   */
  stacked?: boolean
  optional?: boolean
  placeholder?: string
  defaultValue?: string | number
  type?: string
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  labelAdornment?: ReactNode
  rules?: RegisterOptions
  readOnly?: boolean
  sx?: any
  inputSx?: any
  formSx?: any
  labelSx?: any
  disabled?: boolean
  pattern?: string
}

/**
 * VERA-291 — now renders through the shared `components/UI/Input` instead of
 * a hand-styled raw MUI `Input`, so height/padding/border/font-size come from
 * the same one place every other field in the app reads from.
 *
 * Bug fixed in the same pass: the caller-supplied `onChange` (used by
 * EditFieldModal's password fields to track local `passwordCurrent`/
 * `passwordNew` state, which gates the Save button) was declared on
 * `FieldProps` but never destructured here, so it silently never fired —
 * the Save button could never enable for a password change. Both the
 * caller's `onChange` and react-hook-form's own `register(name).onChange`
 * are now invoked.
 *
 * ⚠️ An earlier version of this comment cited `Fields/TextField.tsx` as "the
 * working adapter pattern" to copy. It is NOT. That component carries the exact
 * bug `d0f5b55e8` fixed here: it builds a synthetic RHF event with no `name`
 * and passes only its own forwardRef to `Input`, never `registerProps.ref` — so
 * it would silently drop every edit. It survives only because it is dead code
 * (its lone importer, `components/pages/certificates/HeaderBar.tsx`, imports it
 * but never renders it). Do not copy from it; copy from here. If anyone wires
 * it up, fix it first.
 */
export const FieldText = ({
  label,
  stacked = false,
  optional,
  name,
  placeholder,
  startAdornment,
  endAdornment,
  rules,
  register,
  formState: { errors },
  readOnly = false,
  defaultValue = '',
  type = 'text',
  sx,
  inputSx,
  formSx,
  labelSx,
  disabled,
  onChange,
}: FieldTextProps) => {
  const registerProps = register(name, rules)
  const fieldError = errors[name]

  return (
    <FormControl error={!!fieldError} sx={{ width: { xs: '100%', md: '50%' }, ...(formSx || {}) }} fullWidth>
      {stacked ? (
        <FieldLabel label={label} optional={optional} />
      ) : (
        label && <Typography variant='body2' sx={labelSx}>{label}</Typography>
      )}
      <Input
        type={type}
        fullWidth
        placeholder={placeholder}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        inputProps={{ readOnly, id: name }}
        defaultValue={defaultValue}
        disabled={disabled}
        error={!!fieldError}
        sx={inputSx ?? sx}
        // `name` and `ref` are BOTH load-bearing for react-hook-form — neither is
        // decoration, and omitting either fails silently rather than loudly.
        //
        // `name`: RHF's change handler resolves the field by `event.target.name`
        // and skips its entire body — including the write into `_formValues` —
        // when that lookup misses. Without it the synthetic event below is
        // accepted and discarded, so the form submits stale values while the UI
        // shows a success toast. That shipped: editing a name, username, or
        // password on Profile saved nothing.
        //
        // `ref`: RHF marks a field unmounted via its `ref(null)` cleanup, which
        // only runs if the ref was attached. Unattached, a closed drawer's
        // `required` rules keep failing every LATER submit on the page, because
        // validateField skips only unmounted fields. Symptom is order-dependent
        // (open the password drawer, then try to save a name), which is exactly
        // why it survives a casual pass.
        name={name}
        ref={registerProps.ref}
        onChange={(value: string) => {
          const event = { target: { value, name } } as unknown as React.ChangeEvent<HTMLInputElement>
          registerProps.onChange(event)
          onChange?.(event)
        }}
        onBlur={registerProps.onBlur as any}
      />
      {fieldError && <FormHelperText sx={{ mt: '4px' }}>{fieldError?.message as string}</FormHelperText>}
    </FormControl>
  )
}
